import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  IconButton,
  Link,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useQuery } from "@apollo/client";
import { GET_CUSTOMER_INTEGRATIONS } from "../../apis/settingsApis/integration-apis/integrationApis";
import { CheckCategory, IntegrationKey, IntegrationLabel } from "../../utils/enums";
import LoadingToast from "../LoadingToast";

export const DEACTIVATED_DISMISS_KEY = "regodit_deactivated_dismiss";

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function readDismissedWithinSevenDays(storageKey) {
  if (typeof window === "undefined") return false;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return false;
    const ts = Number(raw);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < SEVEN_DAYS_MS;
  } catch {
    return false;
  }
}

/** AWS, Azure, GCP — order used on the main Infrastructure landing page. */
export const ALL_INFRA_CLOUD_INTEGRATION_KEYS = [
  IntegrationKey.AWS,
  IntegrationKey.AZURE,
  IntegrationKey.GCP,
];

const resolveKeysFromCheckCategory = (checkCategory) => {
  const checkCategoryLower = (checkCategory || "").toLowerCase();
  const isAwsCheck = checkCategoryLower.includes(
    CheckCategory?.AWS_SYSTEM_CHECK
  );
  const isAzureCheck = checkCategoryLower.includes(
    CheckCategory?.AZURE_SYSTEM_CHECK
  );
  const isGcpCheck = checkCategoryLower.includes(
    CheckCategory?.GCP_SYSTEM_CHECK
  );

  if (isAwsCheck) return [IntegrationKey.AWS];
  if (isGcpCheck) return [IntegrationKey.GCP];
  if (isAzureCheck) return [IntegrationKey.AZURE];
  return [];
};


const DeactivatedIntegrationsAlert = ({
  checkCategory = "",
  integrationKeys = null,
  onManageIntegrations,
  dismissalStorageKey = DEACTIVATED_DISMISS_KEY,
  allowDismiss = true,
}) => {
  const theme = useTheme();

  const [hiddenByDismissal, setHiddenByDismissal] = React.useState(() =>
    readDismissedWithinSevenDays(dismissalStorageKey)
  );
  const [reminderToastOpen, setReminderToastOpen] = React.useState(false);
  const reminderToastTimerRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (reminderToastTimerRef.current != null) {
        window.clearTimeout(reminderToastTimerRef.current);
      }
    };
  }, []);

  const keysToEvaluate = React.useMemo(() => {
    if (Array.isArray(integrationKeys) && integrationKeys.length > 0) {
      return integrationKeys;
    }
    return resolveKeysFromCheckCategory(checkCategory);
  }, [integrationKeys, checkCategory]);

  const { data: integrationsData } = useQuery(GET_CUSTOMER_INTEGRATIONS);
  const integrations =
    integrationsData?.getAccountDetails?.userIntegrationsDetails?.list || [];

  const deactivatedIntegrations = React.useMemo(() => {
    if (!keysToEvaluate.length) return [];

    const result = [];
    for (const integrationKey of keysToEvaluate) {
      const deactivatedAccounts = integrations.filter(
        (int) => int.integration_key === integrationKey && !int.is_active
      );
      if (deactivatedAccounts.length === 0) continue;
      result.push({
        name: IntegrationLabel[integrationKey],
        key: integrationKey,
        accounts: deactivatedAccounts.map(
          (acc) => acc.account_label || "Default Account"
        ),
      });
    }
    return result;
  }, [integrations, keysToEvaluate]);

  const handleAlertClose = React.useCallback(
    (_event) => {
      try {
        localStorage.setItem(dismissalStorageKey, String(Date.now()));
      } catch {
      }
      setHiddenByDismissal(true);
      setReminderToastOpen(true);
      if (reminderToastTimerRef.current != null) {
        window.clearTimeout(reminderToastTimerRef.current);
      }
      reminderToastTimerRef.current = window.setTimeout(() => {
        setReminderToastOpen(false);
        reminderToastTimerRef.current = null;
      }, 4500);
    },
    [dismissalStorageKey]
  );

  if (!deactivatedIntegrations.length) return null;

  const errColor = theme.customColors?.status?.error?.main || "#EF4444";
  const errBg = theme.customColors?.background?.error || "#FEF2F2";

  /* Match InformationStepsCard: heading + subtitle typography */
  const headingSx = {
    fontWeight: 600,
    color: theme.customColors.text.strong,
    lineHeight: 1.35,
    letterSpacing: "-0.01em",
    fontSize: "0.875rem",
  };
  const bodySx = {
    color: theme.customColors.text.secondary,
    lineHeight: 1.45,
    letterSpacing: "-0.005em",
    fontSize: "0.875rem",
    fontWeight: 400,
  };

  const manageHereLinkSx = {
    ...bodySx,
    fontWeight: 600,
    fontSize: "0.8125rem",
    color: theme.palette.primary.main,
    cursor: "pointer",
    textDecoration: "none",
    padding: "1px 3px",
    display: "inline",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#E3EDFA",
    },
  };
  const accountTextSize = "0.8125rem";

  /* Only soften account ID styling — banner / icon stay as-is */
  const accountChipSx = {
    ...bodySx,
    fontSize: accountTextSize,
    fontWeight: 600,
    color: "#F87171", 
    padding: "0 4px",
    borderRadius: "4px",
    display: "inline",
    mx: 0.125,
    wordBreak: "break-word",
    lineHeight: 1.35,
  };

  const accountRowSx = {
    ...bodySx,
    fontSize: accountTextSize,
    lineHeight: 1.5,
  };

  const titleText =
    deactivatedIntegrations.length === 1
      ? `${deactivatedIntegrations[0].name} integration is inactive.`
      : "Some cloud integrations are inactive.";

  /** Single provider → "Manage it here."; multiple → "Manage them." */
  const renderManageIntegrationsLink = (isIndividual) => (
    <Box
      component="span"
      onClick={onManageIntegrations}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onManageIntegrations?.();
        }
      }}
      sx={manageHereLinkSx}
    >
      {isIndividual ? "Manage it here." : "Manage them."}
    </Box>
  );

  return (
    <>
    {!hiddenByDismissal && (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        px: 2,
        py: 1.25,
        borderRadius: "10px",
        backgroundColor: errBg,
        border: `1px solid ${errColor}25`,
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        transition: "box-shadow 0.2s ease-in-out",
        "&:hover": {
          boxShadow: "0 2px 8px rgba(239, 68, 68, 0.08)",
        },
      }}
    >
      <InfoOutlinedIcon
        sx={{
          fontSize: 18,
          color: errColor,
          mt: 0.1,
          flexShrink: 0,
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0, pr: allowDismiss ? 3.5 : 0 }}>
        {deactivatedIntegrations.length > 1 ? (
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "baseline",
                columnGap: 1,
                rowGap: 0.25,
                mb: 1,
              }}
            >
              <Typography component="span" sx={{ ...headingSx, display: "inline" }}>
                {titleText}
              </Typography>
              {renderManageIntegrationsLink(false)}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.85,
              }}
            >
              {deactivatedIntegrations.map((integration) => (
                <Typography
                  key={integration.key}
                  component="div"
                  variant="body2"
                  sx={accountRowSx}
                >
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: theme.customColors.text.strong }}
                  >
                    {integration.name}
                  </Box>
                  {integration.accounts.length > 0 && (
                    <>
                      {" "}
                      —{" "}
                      {integration.accounts.length === 1 ? "account" : "accounts"}:{" "}
                      <Box component="span" sx={accountChipSx}>
                        {integration.accounts.join(", ")}
                      </Box>
                    </>
                  )}
                </Typography>
              ))}
            </Box>
          </>
        ) : deactivatedIntegrations[0].accounts.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "baseline",
                columnGap: 1,
                rowGap: 0.25,
                mb: 0.4,
              }}
            >
              <Typography component="span" sx={{ ...headingSx, display: "inline" }}>
                {titleText}
              </Typography>
              {renderManageIntegrationsLink(true)}
            </Box>
            <Typography component="div" variant="body2" sx={accountRowSx}>
              {deactivatedIntegrations[0].accounts.length === 1
                ? "Account"
                : "Accounts"}
              :{" "}
              <Box component="span" sx={accountChipSx}>
                {deactivatedIntegrations[0].accounts.join(", ")}
              </Box>
            </Typography>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              columnGap: 1,
              rowGap: 0.25,
            }}
          >
            <Typography component="span" sx={{ ...headingSx, display: "inline" }}>
              {titleText}
            </Typography>
            {renderManageIntegrationsLink(true)}
          </Box>
        )}
      </Box>

      {allowDismiss && (
        <Tooltip
          title="Dismiss — we'll remind you again in 7 days"
          arrow
          placement="top"
          enterDelay={300}
        >
          <IconButton
            size="small"
            aria-label="Dismiss — snooze for 7 days"
            onClick={handleAlertClose}
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              color: theme.customColors.text.secondary,
              transition: "background-color 0.15s ease, color 0.15s ease",
              "&:hover": {
                color: errColor,
                backgroundColor: "rgba(239, 68, 68, 0.14)",
              },
              "&:focus-visible": {
                color: errColor,
                backgroundColor: "rgba(239, 68, 68, 0.1)",
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    )}
    <LoadingToast
      open={reminderToastOpen}
      variant="reminder"
      title="Reminder saved"
      message={
        <>
          We&apos;ll show this again in 7 days. You can still manage integrations anytime from{" "}
          <Link
            component={RouterLink}
            to="/integrations?domain=cloud_provider"
            underline="hover"
            sx={{ fontWeight: 600 }}
          >
            Integrations
          </Link>
          .
        </>
      }
    />
    </>
  );
};

export default DeactivatedIntegrationsAlert;
